import { useEffect, useRef, useState } from "react";
import { Eye, EyeOff, Images, Pencil, Trash2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { API, errMsg, imageRatioClass, imgSrc, normalizeImageRatio, uploadFile } from "@/lib/api";
import ImageRatioPicker from "@/components/ImageRatioPicker";

const inputCls = "w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A227]";
const labelCls = "mb-1.5 block text-xs font-bold uppercase tracking-widest text-slate-500";
const ACCEPTED_TYPES = new Set(["image/jpeg", "image/png", "image/webp", "image/gif"]);
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

const titleFromFile = (name) =>
  name.replace(/\.[^.]+$/, "").replace(/[-_]+/g, " ").replace(/\s+/g, " ").trim() || "Foto Galeri";

export default function GalleryAdmin() {
  const [photos, setPhotos] = useState(null);
  const [form, setForm] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState("");
  const bulkRef = useRef(null);
  const replaceRef = useRef(null);

  const load = () => API.get("/gallery/all").then((response) => setPhotos(response.data || [])).catch(() => setPhotos([]));

  useEffect(() => {
    load();
  }, []);

  const validateFile = (file) => {
    if (!ACCEPTED_TYPES.has(file.type)) return `${file.name}: format harus JPG, PNG, WebP, atau GIF.`;
    if (file.size > MAX_IMAGE_BYTES) return `${file.name}: ukuran maksimal 5MB.`;
    return "";
  };

  const uploadMany = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;

    const invalid = files.map(validateFile).filter(Boolean);
    if (invalid.length) {
      toast.error(invalid[0]);
      return;
    }

    setUploading(true);
    let completed = 0;
    const failures = [];
    const firstOrder = Math.max(0, ...(photos || []).map((photo) => Number(photo.sort_order) || 0)) + 1;

    for (let index = 0; index < files.length; index += 1) {
      const file = files[index];
      setProgress(`Mengunggah ${index + 1} dari ${files.length}`);
      try {
        const imageUrl = await uploadFile(file);
        await API.post("/gallery", {
          title: titleFromFile(file.name),
          description: "",
          image_url: imageUrl,
          image_ratio: "1:1",
          sort_order: firstOrder + index,
          is_published: true,
        });
        completed += 1;
      } catch (error) {
        failures.push(`${file.name}: ${errMsg(error)}`);
      }
    }

    setUploading(false);
    setProgress("");
    load();
    if (completed) toast.success(`${completed} foto berhasil diunggah dan langsung ditampilkan.`);
    if (failures.length) toast.error(`${failures.length} foto gagal. ${failures[0]}`);
  };

  const replaceImage = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const invalid = validateFile(file);
    if (invalid) {
      toast.error(invalid);
      return;
    }
    setUploading(true);
    setProgress("Mengunggah gambar pengganti");
    try {
      const imageUrl = await uploadFile(file);
      setForm((current) => ({ ...current, image_url: imageUrl }));
      toast.success("Gambar pengganti berhasil diunggah.");
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setUploading(false);
      setProgress("");
    }
  };

  const save = async () => {
    if (!form?.image_url?.trim()) return;
    try {
      await API.put(`/gallery/${form.id}`, {
        title: form.title.trim() || "Foto Galeri",
        description: form.description.trim(),
        image_url: form.image_url.trim(),
        image_ratio: normalizeImageRatio(form.image_ratio, "1:1"),
        sort_order: Number(form.sort_order) || 999,
        is_published: form.is_published !== false,
      });
      toast.success("Foto galeri diperbarui.");
      setForm(null);
      load();
    } catch (error) {
      toast.error(errMsg(error));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus foto ini dari galeri? File asli di penyimpanan tidak akan dihapus.")) return;
    try {
      await API.delete(`/gallery/${id}`);
      toast.success("Foto dihapus dari galeri.");
      load();
    } catch (error) {
      toast.error(errMsg(error));
    }
  };

  const togglePublish = async (photo) => {
    try {
      await API.put(`/gallery/${photo.id}`, { is_published: !photo.is_published });
      toast.success(photo.is_published ? "Foto disembunyikan." : "Foto ditampilkan.");
      load();
    } catch (error) {
      toast.error(errMsg(error));
    }
  };

  return (
    <div data-testid="admin-gallery">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-[#0B1F3A]">Kelola Galeri</h1>
          <p className="text-sm text-slate-500">Upload foto sebanyak yang dibutuhkan. Hanya akun pemilik yang dapat mengelola galeri.</p>
        </div>
        <button
          type="button"
          onClick={() => bulkRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-2 rounded-md bg-[#0B1F3A] px-5 py-2.5 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-60"
          data-testid="upload-gallery"
        >
          <Upload className="h-4 w-4" /> {uploading ? progress || "Mengunggah..." : "Upload Foto"}
        </button>
        <input
          ref={bulkRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple
          className="hidden"
          onChange={uploadMany}
          data-testid="gallery-file-input"
        />
      </div>

      <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-xs leading-relaxed text-blue-800">
        Bisa memilih banyak foto sekaligus. Tidak ada batas jumlah foto dari aplikasi; setiap file maksimal 5MB dengan format JPG, PNG, WebP, atau GIF.
      </div>

      <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
        <table className="w-full min-w-[680px] text-sm" data-testid="gallery-table">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-widest text-slate-400">
              <th className="px-5 py-3">Foto</th>
              <th className="px-5 py-3">Urutan</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(photos || []).map((photo) => (
              <tr key={photo.id} className="hover:bg-slate-50">
                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    <img src={imgSrc(photo.image_url)} alt="" className="h-14 w-14 shrink-0 rounded object-cover" />
                    <div className="min-w-0">
                      <p className="max-w-md truncate font-semibold">{photo.title || "Foto Galeri"}</p>
                      <p className="max-w-md truncate text-xs text-slate-400">{photo.description || "Tanpa deskripsi"}</p>
                    </div>
                  </div>
                </td>
                <td className="px-5 py-3 font-mono-accent text-xs">{photo.sort_order ?? 999}</td>
                <td className="px-5 py-3">
                  <span className={`rounded-full px-2 py-1 text-[10px] font-bold ${photo.is_published ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>
                    {photo.is_published ? "TAMPIL" : "DRAFT"}
                  </span>
                </td>
                <td className="whitespace-nowrap px-5 py-3 text-right">
                  <button type="button" onClick={() => togglePublish(photo)} title={photo.is_published ? "Sembunyikan" : "Tampilkan"} className="p-1.5 text-slate-400 hover:text-emerald-600">
                    {photo.is_published ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                  <button type="button" onClick={() => setForm({ ...photo, title: photo.title || "", description: photo.description || "", image_ratio: normalizeImageRatio(photo.image_ratio, "1:1"), sort_order: photo.sort_order ?? 999, is_published: photo.is_published !== false })} title="Edit" className="p-1.5 text-slate-400 hover:text-[#0B1F3A]">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button type="button" onClick={() => remove(photo.id)} title="Hapus" className="p-1.5 text-slate-400 hover:text-red-600">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
            {photos && photos.length === 0 && (
              <tr>
                <td colSpan={4} className="px-5 py-12 text-center text-slate-400">
                  <Images className="mx-auto mb-3 h-8 w-8" />
                  Belum ada foto. Klik “Upload Foto” untuk menambahkan galeri.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {form && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => !uploading && setForm(null)} />
          <div className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-md border border-slate-200 bg-white p-6">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="font-bold">Edit Foto Galeri</h2>
              <button type="button" onClick={() => setForm(null)} disabled={uploading} aria-label="Tutup"><X className="h-5 w-5 text-slate-400" /></button>
            </div>
            <div className="space-y-4">
              <div className={`${imageRatioClass(form.image_ratio, "1:1")} w-full max-w-md overflow-hidden rounded-md bg-slate-100`}>
                <img src={imgSrc(form.image_url)} alt={form.title || "Preview foto"} className="h-full w-full object-cover" />
              </div>
              <div>
                <label className={labelCls}>Gambar</label>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <input value={form.image_url} onChange={(event) => setForm({ ...form, image_url: event.target.value })} className={inputCls} placeholder="URL gambar" />
                  <button type="button" onClick={() => replaceRef.current?.click()} disabled={uploading} className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-md border border-slate-300 px-4 py-2.5 text-xs font-bold disabled:opacity-50">
                    <Upload className="h-4 w-4" /> {uploading ? progress || "Mengunggah..." : "Ganti Foto"}
                  </button>
                  <input ref={replaceRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={replaceImage} />
                </div>
                <ImageRatioPicker
                  value={form.image_ratio}
                  onChange={(value) => setForm({ ...form, image_ratio: value })}
                  defaultRatio="1:1"
                  testid="gallery-image-ratio"
                />
                <p className="mt-1 text-[10px] text-slate-400">Rasio hanya mengatur tampilan dan tidak memotong file gambar asli.</p>
              </div>
              <div>
                <label className={labelCls}>Judul Foto</label>
                <input value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} className={inputCls} data-testid="gallery-title" />
              </div>
              <div>
                <label className={labelCls}>Deskripsi</label>
                <textarea rows={3} value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} className={inputCls} data-testid="gallery-description" />
              </div>
              <div>
                <label className={labelCls}>Urutan Tampil</label>
                <input type="number" min="1" value={form.sort_order} onChange={(event) => setForm({ ...form, sort_order: event.target.value })} className={inputCls} />
                <p className="mt-1 text-[10px] text-slate-400">Angka lebih kecil tampil lebih dahulu.</p>
              </div>
              <label className="flex cursor-pointer items-center gap-2 text-sm">
                <input type="checkbox" checked={form.is_published} onChange={(event) => setForm({ ...form, is_published: event.target.checked })} className="h-4 w-4 accent-[#0B1F3A]" />
                Tampilkan di website
              </label>
              <button type="button" onClick={save} disabled={uploading || !form.image_url.trim()} className="w-full rounded-md bg-gold px-6 py-3 text-sm font-bold text-[#0B1F3A] disabled:opacity-50" data-testid="save-gallery">
                <Images className="mr-2 inline h-4 w-4" /> Simpan Perubahan
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
